import {Theme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useState} from 'react';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import {OPERATOR, WhereConstraints} from '../../../graphql/common.datatypes';
import getGroupMembers from './getGroupMembers.api';
import GroupMemberSearchFilter, {FilterValues} from './GroupMemberSearchFilter';
import Results from './Results';
import {GroupMember} from '../../../graphql/groups.datatypes';
import getGroupMembersInMyGroup from './getGroupMembersInMyGroup.api';
import getMyTravellers from './getMyTravellers.api';
import {formatGraphQlDate} from '../../../utils/formatters';
import moment from 'moment';
import getMyGroupMembers from './getMyGroupMembers.api';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  results: {
    marginTop: theme.spacing(3)
  }
}));

// export interface FilterValues {
// }

export const GroupMemberListContent = (props: {
  groupId?: number,
  userId?: number,
  billingPartyId?: number,
  roomPartyId?: number,
  action?: (groupMember: GroupMember) => void,
  setModal?: (modalSelection: string) => void,
  actionTitle?: string,
  groupRef?: string,
  canEdit: boolean,
  isAdmin: boolean,
  myGroupMembers?: boolean,
  selectedMember?: GroupMember,
  setSelectedMember?: (groupMember: GroupMember) => void,
}) => {
  const {groupId, billingPartyId, roomPartyId, isAdmin, canEdit, groupRef, myGroupMembers, selectedMember, setSelectedMember} = props;
  const classes = useStyles();
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const hideGroupColumn = groupId !== undefined || billingPartyId !== undefined || roomPartyId !== undefined;

  let hasRoomMember: WhereConstraints | null = null;
  if (props.billingPartyId){
    if(!hasRoomMember) hasRoomMember = {AND: []};
    hasRoomMember.AND!.push({
      column: 'BILLING_PARTY_ID',
      operator: OPERATOR.EQ,
      value: props.billingPartyId
    });
  }
  if (props.roomPartyId){
    if(!hasRoomMember) hasRoomMember = {AND: []};
    hasRoomMember.AND!.push({
      column: 'ROOM_PARTY_ID',
      operator: OPERATOR.EQ,
      value: props.roomPartyId
    });
  }
  if (!isAdmin) {
    if(!hasRoomMember) hasRoomMember = {AND: []};
    const threeYears = formatGraphQlDate(moment().subtract((365 * 3), 'days'));
    hasRoomMember.AND!.push({
      column: 'END_DATE',
      operator: OPERATOR.GTE,
      value: threeYears
    });
  }

  const addWhereGroupId = (constraints: WhereConstraints) => {
    const whereConstraints = {...constraints}
    if (!whereConstraints.AND) whereConstraints.AND = [];
    if (props.groupId) {
      whereConstraints.AND.push({column: 'group_id', operator: OPERATOR.EQ, value: props.groupId});
    }
    if (props.userId) {
      whereConstraints.AND.push({column: 'user_id', operator: OPERATOR.EQ, value: props.userId});
    }

    return whereConstraints;
  }

  const [orderBy] = useState([{column: 'created_at', order: 'DESC'}]);

  const initialFilters: FilterValues = {
    email: '',
    cellphone: '',
    first_name: '',
    last_name: '',
    validated_true: false,
    validated_false: false,
    disabled_true: false,
    disabled_false: true,
    tour_role: -1,
    cancelled_true: false,
    cancelled_false: false,
    authorized_true: false,
    authorized_false: false,
  };
  const [filters, setFilters] = useState(initialFilters);

  const applyFilters = (newFilters: FilterValues) => {
    setFilters(newFilters);
    setWhere(getConstraintsFromFilters(newFilters));
  };

  const getConstraintsFromFilters = (newFilters: FilterValues) => {
    let whereConstraints: WhereConstraints = {AND: [],};

    if (newFilters.first_name !== '') whereConstraints.AND!.push({
      column: 'first_name',
      operator: OPERATOR.LIKE,
      value: `%${newFilters.first_name}%`
    });
    if (newFilters.last_name !== '') whereConstraints.AND!.push({
      column: 'last_name',
      operator: OPERATOR.LIKE,
      value: `%${newFilters.last_name}%`
    });
    if (newFilters.email !== '') whereConstraints.AND!.push({
      column: 'email',
      operator: OPERATOR.LIKE,
      value: `%${newFilters.email}%`
    });
    if (newFilters.cellphone !== '') whereConstraints.AND!.push({
      column: 'cellphone',
      operator: OPERATOR.LIKE,
      value: `%${newFilters.cellphone}%`
    });

    if (newFilters.tour_role > -1) whereConstraints.AND!.push({
      column: 'tour_role',
      operator: OPERATOR.EQ,
      value: newFilters.tour_role
    });
    if (newFilters.authorized_true) whereConstraints.AND!.push({
      column: 'authorised',
      operator: OPERATOR.EQ,
      value: true
    });
    if (newFilters.authorized_false) whereConstraints.AND!.push({
      column: 'authorised',
      operator: OPERATOR.EQ,
      value: false
    });
    if (newFilters.validated_true) whereConstraints.AND!.push({
      column: 'validated',
      operator: OPERATOR.EQ,
      value: true
    });
    if (newFilters.validated_false) whereConstraints.AND!.push({
      column: 'validated',
      operator: OPERATOR.EQ,
      value: false
    });
    if (newFilters.disabled_true) whereConstraints.AND!.push({column: 'disabled', operator: OPERATOR.EQ, value: true});
    if (newFilters.disabled_false) whereConstraints.AND!.push({
      column: 'disabled',
      operator: OPERATOR.EQ,
      value: false
    });
    if (newFilters.cancelled_true) whereConstraints.AND!.push({
      column: 'cancelled',
      operator: OPERATOR.EQ,
      value: true
    });
    if (newFilters.cancelled_false) whereConstraints.AND!.push({
      column: 'cancelled',
      operator: OPERATOR.EQ,
      value: false
    });

    whereConstraints = addWhereGroupId(whereConstraints)

    return whereConstraints;
  };

  const [where, setWhere] = useState(getConstraintsFromFilters(initialFilters));
  const groupMembersData = myGroupMembers? getMyGroupMembers(rowsPerPage, page, where, orderBy, hasRoomMember):
      isAdmin ? getGroupMembers(rowsPerPage, page, where, orderBy, hasRoomMember) :
      groupRef ? getGroupMembersInMyGroup(groupRef!, rowsPerPage, page, where, orderBy, hasRoomMember) :
          getMyTravellers(rowsPerPage, page, where, orderBy, hasRoomMember);
  let groupMembers
  if (groupMembersData.data && groupMembersData.data.groupMembers) groupMembers = groupMembersData.data.groupMembers;
  if (groupMembersData.data && groupMembersData.data.groupMembersInMyGroup) groupMembers = groupMembersData.data.groupMembersInMyGroup;
  if (groupMembersData.data && groupMembersData.data.myTravellers) groupMembers = groupMembersData.data.myTravellers;
  if (groupMembersData.data && groupMembersData.data.myGroupMembers) groupMembers = groupMembersData.data.myGroupMembers;

  return (
      <React.Fragment>
        {(isAdmin || myGroupMembers) && (
            <SearchBar filterContent={
              <GroupMemberSearchFilter filters={filters} initialFilters={initialFilters} applyFilters={applyFilters}/>
            }
            />
        )}
        <LoadingOverlay loading={groupMembersData.loading}>
          {groupMembers && groupMembers.data && (
              <Results
                className={classes.results}
                groupsMembers={groupMembers.data}
                paginatorInfo={groupMembers.paginatorInfo}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                action={props.action}
                actionTitle={props.actionTitle}
                setModal={props.setModal}
                hideGroupColumn={hideGroupColumn}
                isAdmin={isAdmin}
                canEdit={canEdit}
                groupRef={groupRef}
                selectedMember={selectedMember}
                setSelectedMember={setSelectedMember}
            />
        )}
      </LoadingOverlay>
    </React.Fragment>
  );
};

const GroupMemberList = (props: {groupId?: number}) => {
  const classes = useStyles();
  const title = 'Group Members';

  return (
      <Page className={classes.root} title={title}>
        <PageHeader section="groups" title={title}/>
        <GroupMemberListContent {...props} isAdmin={true} canEdit={true}/>
      </Page>
  );
};

export default GroupMemberList;
